<script setup lang="ts">
  /**
   * PDFViewer Component
   * A reusable component for displaying PDF documents in a modal dialog.
   *
   * Features:
   * - Modal PDF viewer with backdrop click to close
   * - Page navigation with input field and arrow buttons
   * - Keyboard navigation (arrow keys for pages, Escape to close)
   * - Text highlighting
   * - Loading state
   * - Responsive design
   * - Automatic scanned PDF detection with fit-to-width
   *
   * @example
   * ```vue
   *   <PDFViewer
   *     v-model:isOpen="isOpen"
   *     v-model:url="pdfUrl"
   *     :filename="filename"
   *     :highlight-text="'text to highlight'"
   *     :initial-page="1"
   *     @loaded="(pdfInstance) => console.log('PDF loaded', pdfInstance)"
   *     @page-change="(page) => console.log('Page changed', page)"
   *   />
   * ```
   *
   * @typedef {Object} Props
   * @property {string} url - URL of the PDF file to display
   * @property {string} filename - Name of the file to display in the header
   * @property {boolean} isOpen - Controls visibility of the modal
   * @property {number} [initialPage=1] - Initial page number to display
   * @property {string} [highlightText] - Text to highlight in the PDF
   * @property {number} [scale=1.1] - Scale factor for the PDF display
   * @property {boolean} [fitToWidth=false] - Whether to fit the PDF to the width of the container
   * @property {Object} [document] - Document metadata for scanned PDF detection
   * @property {number} [document.filesize] - File size in bytes (required for detection)
   * @property {number} [document.page_count] - Number of pages in the document (optional, improves detection accuracy)
   *
   * @typedef {Object} Emits
   * @property {(event: 'update:isOpen', value: boolean) => void} update:isOpen - Emitted when modal visibility changes
   * @property {(event: 'update:url', value: string) => void} update:url - Emitted when URL should be updated
   * @property {(event: 'loaded', pdfInstance: any) => void} loaded - Emitted when PDF is loaded
   * @property {(event: 'pageChange', page: number) => void} pageChange - Emitted when page changes
   * @property {(event: 'highlight', event: any) => void} highlight - Emitted on text highlight
   */

  import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
  import { usePDF, VuePDF } from '@tato30/vue-pdf'
  import PDFNavigation from '@/components/pdf/genericPdfNav.vue'
  import { ZoomIn, ZoomOut, X, Maximize, RotateCcw } from 'lucide-vue-next'

  interface Props {
    url: string
    filename: string
    isOpen: boolean
    initialPage?: number
    highlightText?: string
    scale?: number
    fitToWidth?: boolean
    document?: {
      filesize?: number
      page_count?: number
    }
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'update:isOpen', value: boolean): void
    (e: 'update:url', value: string): void
    (e: 'loaded', pdfInstance: any): void
    (e: 'pageChange', page: number): void
    (e: 'highlight', event: any): void
  }>()

  const currentPage = ref(props.initialPage || 1)
  const isLoading = ref(true)
  const internalUrl = ref(props.url)
  const currentScale = ref(props.scale ?? 1.0)
  const fitToWidth = ref(props.fitToWidth ?? false)
  const containerRef = ref<HTMLElement | null>(null)
  const pdfKey = ref(0) // Force re-render when switching modes

  // Detect if PDF is scanned (image-based) based on filesize per page
  const isScannedPDF = computed(() => {
    const doc = props.document
    if (!doc || !doc.filesize) return false

    // If filesize and page count are available, use them to estimate if it's an image-based PDF
    if (doc.page_count && doc.page_count > 0) {
      // Image-based PDFs typically have much larger filesize per page
      const fileSizePerPage = doc.filesize / doc.page_count
      // If average page size is over 1MB (1,000,000 bytes), likely image-based
      return fileSizePerPage > 1000000
    }

    // If we only have filesize (e.g., for uploaded files), use a simpler heuristic
    // Files larger than 5MB are likely to be scanned/image-based PDFs
    return doc.filesize > 5000000
  })

  const highlightOptions = computed(() => ({
    ignoreCase: true,
    completeWords: true
  }))

  // Watch for URL changes from parent
  watch(
    () => props.url,
    (newUrl) => {
      internalUrl.value = newUrl
      // Reset fitToWidth state when a new document is loaded
      if (newUrl) {
        fitToWidth.value = props.fitToWidth ?? false
        currentScale.value = props.scale ?? 1.0
      }
    }
  )

  // Watch for fitToWidth changes from parent
  watch(
    () => props.fitToWidth,
    (newValue, oldValue) => {
      if (newValue !== oldValue) {
        fitToWidth.value = newValue
      }
    }
  )

  // Auto-enable fit-to-width for scanned PDFs
  watch(
    [isScannedPDF, () => props.url],
    ([isScanned, url]) => {
      // Only auto-enable if we have a URL (document is loaded) and it's detected as scanned
      // and the parent hasn't explicitly set fitToWidth
      if (url && isScanned && !props.fitToWidth) {
        fitToWidth.value = true
        pdfKey.value++
      }
    },
    { immediate: true }
  )

  // Use internal URL for PDF loading
  const { pdf, pages } = usePDF(internalUrl)

  function closeViewer() {
    emit('update:isOpen', false)
    emit('update:url', '')
    internalUrl.value = ''
    currentPage.value = props.initialPage || 1
    isLoading.value = true
  }

  function onPdfLoaded(pdfInstance: any) {
    isLoading.value = false
    emit('loaded', pdfInstance)
  }

  function toggleFitToWidth() {
    fitToWidth.value = !fitToWidth.value
    // Force re-render when switching modes
    pdfKey.value++
    // When disabling fit-to-width, we reset to the default scale
    if (!fitToWidth.value) {
      currentScale.value = props.scale ?? 1.0
    }
  }

  function zoomIn() {
    if (fitToWidth.value) {
      fitToWidth.value = false
      pdfKey.value++
    }
    currentScale.value = Math.min(5, currentScale.value + 0.1)
  }

  function zoomOut() {
    if (fitToWidth.value) {
      fitToWidth.value = false
      pdfKey.value++
    }
    currentScale.value = Math.max(0.1, currentScale.value - 0.1)
  }

  function resetZoom() {
    fitToWidth.value = false
    currentScale.value = props.scale ?? 1.0
    pdfKey.value++
  }

  // Calculate container width on resize
  let resizeObserver: ResizeObserver | null = null

  onMounted(() => {
    if (containerRef.value) {
      resizeObserver = new ResizeObserver(() => {
        // Force re-render when container size changes to update fit-to-width
        if (fitToWidth.value) {
          // Toggle and re-toggle to force re-render
          fitToWidth.value = false
          setTimeout(() => {
            fitToWidth.value = true
          }, 10)
        }
      })
      resizeObserver.observe(containerRef.value)
    }

    // Add keyboard event listener
    document.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    if (resizeObserver && containerRef.value) {
      resizeObserver.unobserve(containerRef.value)
      resizeObserver.disconnect()
    }

    // Remove keyboard event listener
    document.removeEventListener('keydown', handleKeydown)
  })

  function updateCurrentPage(newPage: number) {
    currentPage.value = newPage
    emit('pageChange', newPage)
  }

  function onHighlight(event: any) {
    emit('highlight', event)
  }

  function handleBackdropClick(event: MouseEvent) {
    // Only close if clicking directly on the backdrop
    if (event.target === event.currentTarget) {
      closeViewer()
    }
  }

  // Keyboard navigation
  function handleKeydown(event: KeyboardEvent) {
    // Only handle arrow keys when the modal is open and not typing in an input
    if (!props.isOpen || (event.target as HTMLElement)?.tagName === 'INPUT') {
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        if (currentPage.value > 1) {
          updateCurrentPage(currentPage.value - 1)
        }
        break
      case 'ArrowRight':
        event.preventDefault()
        if (currentPage.value < pages.value) {
          updateCurrentPage(currentPage.value + 1)
        }
        break
      case 'Escape':
        event.preventDefault()
        closeViewer()
        break
    }
  }
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm"
    @click="handleBackdropClick"
  >
    <div class="flex h-[95vh] w-[45vw] flex-col overflow-hidden rounded-lg bg-white shadow-lg">
      <!-- Header -->
      <div
        class="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-6"
      >
        <h2 class="truncate text-base font-medium text-gray-900">{{ filename }}</h2>
        <button
          type="button"
          class="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition
            hover:bg-gray-100 hover:text-gray-500"
          @click="closeViewer"
        >
          <span class="sr-only">Close</span>
          <X class="h-5 w-5" />
        </button>
      </div>

      <!-- Content -->
      <div class="relative flex-1 overflow-hidden bg-white">
        <div
          v-if="isLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white/90"
        >
          <div class="flex items-center gap-3">
            <div
              class="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"
            ></div>
            <span class="text-sm font-medium text-gray-600">Loading document...</span>
          </div>
        </div>
        <div
          ref="containerRef"
          class="h-full w-full overflow-auto"
        >
          <div class="flex min-h-full w-full items-center justify-center">
            <div class="w-full max-w-3xl px-6">
              <VuePDF
                :key="pdfKey"
                :pdf="pdf"
                :page="currentPage"
                :scale="fitToWidth ? undefined : currentScale"
                :highlight-text="highlightText"
                :highlight-options="highlightOptions"
                :fit-parent="fitToWidth"
                text-layer
                class="mx-auto w-full"
                :style="{
                  maxWidth: fitToWidth ? '100%' : 'none',
                  height: 'auto'
                }"
                @loaded="onPdfLoaded"
                @highlight="onHighlight"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex h-14 shrink-0 items-center justify-between border-t border-gray-200 bg-white px-6"
      >
        <!-- Zoom controls -->
        <div class="flex items-center space-x-2">
          <button
            class="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white
              text-gray-700 hover:bg-gray-50"
            title="Zoom out"
            @click="zoomOut"
          >
            <span class="sr-only">Zoom out</span>
            <ZoomOut class="h-4 w-4" />
          </button>

          <span class="text-sm text-gray-600">{{ Math.round(currentScale * 100) }}%</span>

          <button
            class="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white
              text-gray-700 hover:bg-gray-50"
            title="Zoom in"
            @click="zoomIn"
          >
            <span class="sr-only">Zoom in</span>
            <ZoomIn class="h-4 w-4" />
          </button>

          <button
            class="ml-1 inline-flex h-8 items-center rounded-md border border-gray-300 bg-white px-3 text-xs
              text-gray-700 hover:bg-gray-50"
            title="Reset zoom"
            @click="resetZoom"
          >
            <RotateCcw class="mr-1 inline h-3 w-3" />
            Reset
          </button>

          <button
            class="ml-1 inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs hover:bg-gray-50"
            :class="
              fitToWidth
                ? 'border-purple-300 bg-purple-100 text-purple-700'
                : 'bg-white text-gray-700'
            "
            title="Fit to width"
            @click="toggleFitToWidth"
          >
            <Maximize class="mr-1 inline h-3 w-3" />
            Fit Width
          </button>
        </div>

        <!-- Page navigation -->
        <PDFNavigation
          :current-page="currentPage"
          :total-pages="pages"
          :show-prev-next-buttons="true"
          :show-page-buttons="true"
          @update-page="updateCurrentPage"
        />
      </div>
    </div>
  </div>
</template>
